import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { SettingsLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Toggle,
  Badge,
} from '../../../design-system/components';
import { AlertTriangle } from 'lucide-react';
import { toggle_admin_settings_event_settings_path } from '../../../lib/routes';

const GROUP_ORDER = ['Application', 'Referral', 'Commission', 'Fraud'];

function groupEvents(events) {
  const groups = {};
  for (const event of events) {
    if (!groups[event.group]) groups[event.group] = [];
    groups[event.group].push(event);
  }
  return GROUP_ORDER.filter((g) => groups[g]).map((g) => ({ name: g, events: groups[g] }));
}

export default function Events({ events, email_configurable }) {
  const [toggling, setToggling] = useState({});
  const grouped = groupEvents(events);

  function handleToggle(eventType, channel) {
    const key = `${channel}.${eventType}`;
    setToggling((prev) => ({ ...prev, [key]: true }));
    router.post(
      toggle_admin_settings_event_settings_path(),
      { event_type: eventType, channel },
      { onFinish: () => setToggling((prev) => ({ ...prev, [key]: false })) },
    );
  }

  return (
    <SettingsLayout>
      <PageHeader
        title="Event Settings"
        description="Control which events trigger webhook deliveries and email notifications."
      />

      {!email_configurable && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 p-4">
          <AlertTriangle size={20} strokeWidth={1.5} className="text-warning-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-warning-800">Email not configured</p>
            <p className="text-sm text-warning-700">
              Set <code className="text-xs bg-warning-100 px-1 py-0.5 rounded">SENDGRID_API_KEY</code> and{' '}
              <code className="text-xs bg-warning-100 px-1 py-0.5 rounded">SENDGRID_FROM_EMAIL</code> environment
              variables to enable email notifications.
            </p>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-6">
        {grouped.map((group) => (
          <Card key={group.name}>
            <CardHeader>
              <h3 className="text-base font-semibold text-fg">{group.name} Events</h3>
            </CardHeader>
            <CardBody className="divide-y divide-edge">
              {group.events.map((event) => {
                const webhookKey = `webhook.${event.type}`;
                const emailKey = `email.${event.type}`;
                return (
                  <div key={event.type} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-fg">
                        <code className="text-xs bg-surface-muted px-1.5 py-0.5 rounded">{event.type}</code>
                      </p>
                      <p className="text-xs text-fg-tertiary mt-1">
                        Email &rarr;{' '}
                        {event.email_recipient === 'admins' ? 'All admins' : 'Affiliate'}
                      </p>
                    </div>
                    <div className="flex items-center gap-6 shrink-0">
                      <div className="flex items-center gap-2">
                        <Toggle
                          checked={event.webhook_enabled}
                          onChange={() => handleToggle(event.type, 'webhook')}
                          disabled={!!toggling[webhookKey]}
                        />
                        <span className="text-xs text-fg-secondary w-14">Webhook</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Toggle
                          checked={event.email_enabled}
                          onChange={() => handleToggle(event.type, 'email')}
                          disabled={!!toggling[emailKey] || !email_configurable}
                        />
                        <span className="text-xs text-fg-secondary w-10">Email</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardBody>
          </Card>
        ))}
      </div>
    </SettingsLayout>
  );
}
